/**
 * Full-width "Developer plan — Coming Soon" strip shown below Clerk's pricing
 * table. The Developer tier isn't a purchasable Clerk plan yet; this is a
 * lead-capture card whose CTA opens the waitlist email dialog. Kept as our own
 * component because Clerk's <PricingTable> only renders real, purchasable plans.
 */
import React from "react";

import { useTheme } from "@mui/material/styles";
import { Box, Button, Chip, Typography } from "@mui/material";

import { CaptureEmailDialog } from "components/common/dialogs";
import PricingIcon from "./PricingIcon";
import { track, EVENTS } from "utils/analytics.js";

const features = [
  "All models included",
  "No limits on problem size",
  "Unlimited runs in the web app (up to 10/min)",
  "Save unlimited models",
  "Docs & forum support",
  "Auto-populate travel times",
  "Access to our REST API",
];

function ComingSoonBanner() {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  return (
    <>
      <Box
        sx={{
          mt: 2,
          p: { xs: 2, md: 3 },
          borderRadius: "1rem",
          border: `1px solid ${theme.palette.gray[200]}`,
          borderTop: `4px solid ${theme.palette.primary.main}`,
          backgroundColor: theme.palette.white.main,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.02)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Chip
            label="Coming Soon"
            size="small"
            sx={{
              height: "20px",
              fontSize: "0.65rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              backgroundColor: theme.palette.primary.extraLight,
              color: theme.palette.primary.extraDark,
              borderRadius: "4px",
              mb: 0.75,
            }}
          />
          <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.dark.main, mb: 0.25 }}>
            Developer
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.gray[700], mb: 1 }}>
            Full-power API access for building routing into your own product. Join the
            waitlist for early access.
          </Typography>
          <Box
            component="ul"
            sx={{
              p: 0,
              m: 0,
              listStyle: "none",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 3,
            }}
          >
            {features.map((text, idx) => (
              <PricingIcon key={"dev-feature-" + idx} subtitle={text} />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: "100%", md: "220px" },
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              track(EVENTS.CTA_CLICKED, { cta_id: "pricing_enterprise_waitlist", page: "pricing" });
              setDialogOpen(true);
            }}
            sx={{
              width: "100%",
              fontWeight: 700,
              fontSize: "0.85rem",
              py: 0.75,
              backgroundColor: "primary.main",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "primary.extraDark",
                boxShadow: "0 4px 12px rgba(0, 146, 139, 0.35)",
              },
            }}
          >
            Join the waitlist
          </Button>
        </Box>
      </Box>

      <CaptureEmailDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        title="Developer Plan Waitlist"
        source="Enterprise Solutions"
        content="Enter your information to join our Developer Plan waitlist"
        useOrganization
        useMessage
      />
    </>
  );
}

export default ComingSoonBanner;
